<script setup>
import { ref } from 'vue'

const props = defineProps({
  total: Number,
  value: Number,
  showInner: Boolean,
  circleColor: {
    type: String,
    default: '#F9F1F1'
  }
})

const length = 50 * Math.PI * 2

let oldValue = props.value
const internValue = ref(props.value)

let animationDuration = 300
let startTime

function updateValue(currentTime) {
  if (oldValue == props.value) return requestAnimationFrame(updateValue)

  if (!startTime) startTime = currentTime

  let elapsed = currentTime - startTime

  if (elapsed < animationDuration) {
    internValue.value = oldValue + (elapsed / animationDuration) * (props.value - oldValue)
  } else {
    startTime = null
    oldValue = props.value
    internValue.value = props.value
  }

  requestAnimationFrame(updateValue)
}

requestAnimationFrame(updateValue)

function filled() {
  return length * (internValue.value / props.total)
}

function empty() {
  return length - filled()
}

function dashArray() {
  return `${filled()} ${empty()}`
}

function rotation() {
  let angle = 360 * (internValue.value / props.total)
  if (angle == 180) {
    angle += 0.1
  }
  return `rotate(${angle})`
}

function toRadians(angle) {
  return angle * (Math.PI / 180)
}

function translation() {
  let angle = toRadians(360 * (internValue.value / props.total))

  let sin = Math.sin(angle)
  let cos = Math.cos(angle)

  let dx = sin * 50
  let dy = -cos * 50

  let x = 55 + dx
  let y = 55 + dy

  return `translate(${x} ${y})`
}

function circleTransform() {
  return `${translation()} ${rotation()} `
}
</script>

<template>
  <div class="circle" :class="{ large: showInner }">
    <svg
      width="200"
      height="200"
      viewBox="-5 -5 115 115"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g transform="translate(40 40)">
        <path
          d="M3.83008 18.0859L13.1907 28.0314L31.912 4.04492"
          stroke="#e2001a"
          stroke-width="5.2"
          stroke-linecap="square"
          id="checkmark"
          :class="{ done: total == value }"
        />
      </g>
      <circle cx="55" cy="55" r="50" :stroke="circleColor" stroke-width="10"></circle>
      <circle
        v-if="showInner"
        id="Ellipse 301"
        cx="55"
        cy="55"
        r="36"
        stroke="#e2001a"
        stroke-width="8"
      />
      <circle
        transform="rotate(-90 55 55)"
        cx="55"
        cy="55"
        r="50"
        stroke="#e2001a"
        stroke-width="10"
        :stroke-dasharray="dashArray()"
        stroke-linecap="round"
        class="bar"
      ></circle>

      <g :transform="circleTransform()" class="head">
        <circle
          cx="0"
          cy="0"
          r="4.1"
          stroke="#e2001a"
          stroke-width="1"
          fill="#F9F1F1"
          filter="url(#dropshadow)"
        />
        <g transform="translate(-3 -3) scale(0.8)">
          <path
            d="M3.20605 0.582031L6.34737 3.72327L3.20606 6.86457"
            stroke="#e2001a"
            stroke-width="0.871324"
          />
          <line
            x1="5.8916"
            y1="3.7257"
            x2="0.441731"
            y2="3.7257"
            stroke="#e2001a"
            stroke-width="0.871324"
          />
        </g>
      </g>

      <defs>
        <filter
          id="dropshadow"
          x="-40%"
          y="-40%"
          width="180%"
          height="180%"
          filterUnits="userSpaceOnUse"
        >
          <feGaussianBlur in="SourceAlpha" stdDeviation="3" />
          <!-- stdDeviation is how much to blur -->
          <feOffset dx="7" dy="0" result="offsetblur" />
          <feOffset dx="-3" dy="0" result="offsetblur" />
          <feComponentTransfer>
            <feFuncA type="linear" slope="0.2" />
            <!-- slope is the opacity of the shadow -->
          </feComponentTransfer>
          <feMerge>
            <feMergeNode />
            <feMergeNode in="SourceGraphic" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
    </svg>
  </div>
</template>

<style>
.circle {
  justify-self: center;
  align-self: center;
  margin-bottom: 30px;
}

.circle svg {
  width: 200px;
  height: 200px;
}

.circle.large svg {
  width: 220px;
  height: 220px;
}

@media screen and (max-width: 768px) {
  .circle svg {
    width: 130px;
    height: 130px;
  }

  .circle.large svg {
    width: 150px;
    height: 150px;
  }
}

#checkmark {
  stroke-dasharray: 0 100;
  transition: stroke-dasharray 0ms ease-in-out 0ms;
  stroke-width: 0;
}

#checkmark.done {
  stroke-dasharray: 100 100;
  transition: all 500ms cubic-bezier(0.65, 0.05, 0.36, 1) 300ms;
  stroke-width: 5;
}
</style>
